import { useEffect, useMemo, useState } from "react";
import { Backdrop, Button, Checkbox, CircularProgress, FormControlLabel, TextField, Typography } from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import Diversity3OutlinedIcon from "@mui/icons-material/Diversity3Outlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import * as ExcelJS from "exceljs";
import dayjs from "dayjs";
import { saveAs } from "file-saver";

import { findAllModalidadesAcao, findAllTemas, findAllTipologias } from "API/apiAcoes";
import {
  findAllAreasAtuacao,
  findAllFuncoesDesempenhadas,
  findAllLotacoes,
  findAllOrgaosAtuacao,
  findAllVinculosEmpregaticios,
} from "API/apiDadosComplementares";
import {
  findAcoesRelatorioGeral,
  findRelatorioTotalQualificados,
  findRelatorioTotalQualificadosCount,
  findTurmasRelatorioGeral,
  RelatorioTotalParticipacoesParams,
} from "API/apiRelatoriosTurma";
import MultiSelectCheck from "components/atoms/MultiSelectField/MultSelectCheck";
import { MultiSelectOption } from "components/atoms/MultiSelectField/MultiSelectOptions";
import SelectField from "components/atoms/SelectField/SelectField";
import { useAppFeedback } from "hooks/useAppFeedback";
import { useLocalidadeStore } from "store/useLocalidadeStore";
import stylesButton from "styles/Button.module.css";
import stylesInput from "styles/Input.module.css";
import { convertListSelect } from "utils/Arrays";
import { getReportPeriodError } from "utils/reportPeriod";
import { addGovernmentReportLogo, prependGovernmentReportHeader } from "utils/reportExportHeader";

import styles from "../Relatorios.module.css";

type RelatorioTotalQualificadosProps = {
  selectedReportLabel: string;
};

type SelectValue = Array<number | string>;

type QualificadosAcaoItem = {
  id?: number | null;
  nome?: string | null;
  data_inicio?: string | null;
  data_fim?: string | null;
  tipologia_id?: number | null;
  tipologia_nome?: string | null;
  percurso_formativo?: string | null;
  modalidade_id?: number | null;
  modalidade_nome?: string | null;
  temas?: Array<{
    id?: number | null;
    nome?: string | null;
    label?: string | null;
  }>;
  turmas?: Array<{
    id?: number | null;
    nome?: string | null;
    data_inicio?: string | null;
    data_fim?: string | null;
    total_qualificados?: number | null;
  }>;
  turmas_nomes?: string[];
  total_qualificados?: number | null;
  total_qualificados_estadual?: number | null;
  total_qualificados_municipal?: number | null;
};

type QualificadosMunicipioItem = {
  municipio_id?: number | null;
  municipio_nome?: string | null;
  regional_sedese_municipio?: string[];
  total_qualificados?: number | null;
  total_qualificados_estadual?: number | null;
  total_qualificados_municipal?: number | null;
  acoes?: QualificadosAcaoItem[];
};

const COR_RACA_OPTIONS: MultiSelectOption[] = [
  { id: "Branca", nome: "Branca" },
  { id: "Preta", nome: "Preta" },
  { id: "Amarela", nome: "Amarela" },
  { id: "Parda", nome: "Parda" },
];

const AMBITO_OPTIONS = [
  { id: 1, nome: "Estadual" },
  { id: 2, nome: "Municipal" },
];

const DEFICIENCIA_OPTIONS = [{ nome: "Sim" }, { nome: "Não" }];

const AGE_OPTIONS: MultiSelectOption[] = Array.from({ length: 62 }, (_, index) => {
  const age = index + 14;
  return { id: age, nome: String(age) };
});

const formatDate = (value?: string | null) => {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "";
};

const joinText = (values: Array<string | null | undefined>, separator = ", ") =>
  values.map((value) => value?.trim()).filter(Boolean).join(separator);

const buildHeader = (worksheet: ExcelJS.Worksheet, columnsLength: number, headerRowNumber = 1) => {
  const headerRow = worksheet.getRow(headerRowNumber);
  headerRow.height = 22;
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF004D40" },
    };
    cell.font = {
      bold: true,
      color: { argb: "FFFFFFFF" },
    };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
  });

  worksheet.autoFilter = {
    from: { row: headerRowNumber, column: 1 },
    to: { row: headerRowNumber, column: columnsLength },
  };
};

const buildWorkbook = (items: QualificadosMunicipioItem[]) => {
  const workbook = new ExcelJS.Workbook();

  const totalGeralColumns = [
    { header: "MUNICÍPIO", key: "municipio", width: 28 },
    { header: "REGIONAL", key: "regional", width: 22 },
    { header: "TOTAL DE QUALIFICADOS NO PERÍODO (CPF'S ÚNICOS)", key: "total", width: 25 },
    { header: "ÂMBITO ESTADUAL", key: "estadual", width: 20 },
    { header: "ÂMBITO MUNICIPAL", key: "municipal", width: 20 },
  ];
  const totalDetalhadoColumns = [
    ...totalGeralColumns,
    { header: "AÇÃO", key: "acao", width: 38 },
    { header: "TURMAS", key: "turmas", width: 40 },
    { header: "TEMAS ABORDADOS", key: "temas", width: 40 },
    { header: "TIPOLOGIA", key: "tipologia", width: 34 },
    { header: "PERCURSO FORMATIVO", key: "percursoFormativo", width: 34 },
    { header: "MODALIDADE", key: "modalidade", width: 22 },
  ];

  const createWorksheet = (
    name: string,
    columns: Array<{ header: string; key: string; width: number }>,
  ) => {
    const worksheet = workbook.addWorksheet(name, {
      views: [{ state: "frozen", ySplit: 5 }],
    });
    worksheet.columns = columns;

    const headerRowNumber = prependGovernmentReportHeader(worksheet, columns.length) + 1;
    buildHeader(worksheet, columns.length, headerRowNumber);

    return { worksheet, headerRowNumber };
  };

  const { worksheet: totalGeralSheet, headerRowNumber: totalGeralHeaderRow } = createWorksheet(
    "Total Geral",
    totalGeralColumns,
  );
  items.forEach((item) => {
    totalGeralSheet.addRow({
      municipio: item.municipio_nome || "",
      regional: joinText(item.regional_sedese_municipio || []),
      total: item.total_qualificados ?? 0,
      estadual: item.total_qualificados_estadual ?? 0,
      municipal: item.total_qualificados_municipal ?? 0,
    });
  });

  const { worksheet: totalDetalhadoSheet, headerRowNumber: totalDetalhadoHeaderRow } = createWorksheet(
    "Total Detalhado",
    totalDetalhadoColumns,
  );
  items.forEach((item) => {
    (item.acoes || []).forEach((acao) => {
      totalDetalhadoSheet.addRow({
        municipio: item.municipio_nome || "",
        regional: joinText(item.regional_sedese_municipio || []),
        total: acao.total_qualificados ?? 0,
        estadual: acao.total_qualificados_estadual ?? 0,
        municipal: acao.total_qualificados_municipal ?? 0,
        acao: acao.nome || "",
        turmas: joinText((acao.turmas || []).map((turma) => turma.nome), "; "),
        temas: joinText((acao.temas || []).map((tema) => tema.nome || tema.label), "; "),
        tipologia: acao.tipologia_nome || "",
        percursoFormativo: acao.percurso_formativo || "",
        modalidade: acao.modalidade_nome || "",
      });
    });
  });

  [
    { worksheet: totalGeralSheet, headerRowNumber: totalGeralHeaderRow },
    { worksheet: totalDetalhadoSheet, headerRowNumber: totalDetalhadoHeaderRow },
  ].forEach(({ worksheet, headerRowNumber }) => {
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber <= headerRowNumber) return;
      row.eachCell((cell, columnNumber) => {
        cell.alignment = {
          vertical: "middle",
          horizontal: columnNumber >= 3 && columnNumber <= 5 ? "center" : "left",
          wrapText: true,
        };
      });
    });
  });

  return workbook;
};

const RelatorioTotalQualificados = ({ selectedReportLabel }: RelatorioTotalQualificadosProps) => {
  const { showError, showSuccess } = useAppFeedback();
  const ensureEstados = useLocalidadeStore((state) => state.ensureEstados);
  const ensureCidades = useLocalidadeStore((state) => state.ensureCidades);

  const [dataInicial, setDataInicial] = useState("");
  const [dataFinal, setDataFinal] = useState("");
  const periodError = getReportPeriodError(dataInicial, dataFinal);
  const periodValidationError = dataInicial && dataFinal ? periodError : null;
  const [acoesSelecionadas, setAcoesSelecionadas] = useState<SelectValue>([]);
  const [turmasSelecionadas, setTurmasSelecionadas] = useState<SelectValue>([]);
  const [todosMunicipiosEstado, setTodosMunicipiosEstado] = useState(false);
  const [municipiosSelecionados, setMunicipiosSelecionados] = useState<SelectValue>([]);
  const [ambitosSelecionados, setAmbitosSelecionados] = useState<SelectValue>([]);
  const [lotacoesSelecionadas, setLotacoesSelecionadas] = useState<SelectValue>([]);
  const [orgaosSelecionados, setOrgaosSelecionados] = useState<SelectValue>([]);
  const [areasSelecionadas, setAreasSelecionadas] = useState<SelectValue>([]);
  const [funcoesSelecionadas, setFuncoesSelecionadas] = useState<SelectValue>([]);
  const [vinculosSelecionados, setVinculosSelecionados] = useState<SelectValue>([]);
  const [tipologiasSelecionadas, setTipologiasSelecionadas] = useState<SelectValue>([]);
  const [temasSelecionados, setTemasSelecionados] = useState<SelectValue>([]);
  const [modalidadesSelecionadas, setModalidadesSelecionadas] = useState<SelectValue>([]);
  const [idadesSelecionadas, setIdadesSelecionadas] = useState<SelectValue>([]);
  const [coresRacasSelecionadas, setCoresRacasSelecionadas] = useState<SelectValue>([]);
  const [possuiDeficiencia, setPossuiDeficiencia] = useState("");

  const [municipiosOptions, setMunicipiosOptions] = useState<MultiSelectOption[]>([]);
  const [acoesOptions, setAcoesOptions] = useState<MultiSelectOption[]>([]);
  const [turmasOptions, setTurmasOptions] = useState<MultiSelectOption[]>([]);
  const [lotacoesOptions, setLotacoesOptions] = useState<MultiSelectOption[]>([]);
  const [orgaosOptions, setOrgaosOptions] = useState<MultiSelectOption[]>([]);
  const [areasOptions, setAreasOptions] = useState<MultiSelectOption[]>([]);
  const [funcoesOptions, setFuncoesOptions] = useState<MultiSelectOption[]>([]);
  const [vinculosOptions, setVinculosOptions] = useState<MultiSelectOption[]>([]);
  const [tipologiasOptions, setTipologiasOptions] = useState<MultiSelectOption[]>([]);
  const [temasOptions, setTemasOptions] = useState<MultiSelectOption[]>([]);
  const [modalidadesOptions, setModalidadesOptions] = useState<MultiSelectOption[]>([]);

  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingAcoes, setLoadingAcoes] = useState(false);
  const [loadingTurmas, setLoadingTurmas] = useState(false);
  const [loadingCount, setLoadingCount] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [estimatedCount, setEstimatedCount] = useState<number | null>(null);

  const possuiAmbitoEstadual = ambitosSelecionados.includes(1);
  const possuiAmbitoMunicipal = ambitosSelecionados.includes(2);

  const selectedMunicipioIds = useMemo(
    () => municipiosSelecionados.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [municipiosSelecionados],
  );
  const selectedAcaoIds = useMemo(
    () => acoesSelecionadas.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [acoesSelecionadas],
  );
  const selectedTurmaIds = useMemo(
    () => turmasSelecionadas.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [turmasSelecionadas],
  );
  const selectedAmbitosIds = useMemo(
    () => ambitosSelecionados.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [ambitosSelecionados],
  );
  const selectedLotacoesIds = useMemo(
    () => lotacoesSelecionadas.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [lotacoesSelecionadas],
  );
  const selectedOrgaosIds = useMemo(
    () => orgaosSelecionados.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [orgaosSelecionados],
  );
  const selectedAreasIds = useMemo(
    () => areasSelecionadas.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [areasSelecionadas],
  );
  const selectedFuncoesIds = useMemo(
    () => funcoesSelecionadas.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [funcoesSelecionadas],
  );
  const selectedVinculosIds = useMemo(
    () => vinculosSelecionados.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [vinculosSelecionados],
  );
  const selectedTipologiasIds = useMemo(
    () => tipologiasSelecionadas.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [tipologiasSelecionadas],
  );
  const selectedTemasIds = useMemo(
    () => temasSelecionados.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [temasSelecionados],
  );
  const selectedModalidadesIds = useMemo(
    () => modalidadesSelecionadas.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [modalidadesSelecionadas],
  );
  const selectedIdades = useMemo(
    () => idadesSelecionadas.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [idadesSelecionadas],
  );
  const selectedCoresRacas = useMemo(
    () => coresRacasSelecionadas.map((item) => String(item)),
    [coresRacasSelecionadas],
  );

  const hasRequiredFiltersForCount = useMemo(
    () =>
      Boolean(
        !periodError &&
          (todosMunicipiosEstado || selectedMunicipioIds.length > 0) &&
          selectedAmbitosIds.length > 0,
      ),
    [periodError, selectedAmbitosIds.length, selectedMunicipioIds.length, todosMunicipiosEstado],
  );

  useEffect(() => {
    const loadOptions = async () => {
      try {
        setLoadingOptions(true);
        const estados = await ensureEstados();
        const estadoMG = estados.find((item) => item.sigla === "MG");
        if (!estadoMG) {
          throw new Error("Estado MG nao encontrado.");
        }

        const [municipiosMg, lotacoes, orgaos, areas, funcoes, vinculos, tipologias, temas, modalidades] =
          await Promise.all([
            ensureCidades({ estado_id: estadoMG.estado_id }),
            findAllLotacoes(),
            findAllOrgaosAtuacao(),
            findAllAreasAtuacao(),
            findAllFuncoesDesempenhadas(),
            findAllVinculosEmpregaticios(),
            findAllTipologias(),
            findAllTemas(),
            findAllModalidadesAcao(),
          ]);

        setMunicipiosOptions(
          convertListSelect(
            [...municipiosMg].sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR")),
            "id",
            "nome",
          ),
        );
        setLotacoesOptions(convertListSelect(lotacoes, "id", "nome"));
        setOrgaosOptions(convertListSelect(orgaos, "id", "nome"));
        setAreasOptions(convertListSelect(areas, "id", "nome"));
        setFuncoesOptions(convertListSelect(funcoes, "id", "nome"));
        setVinculosOptions(convertListSelect(vinculos, "id", "nome"));
        setTipologiasOptions(convertListSelect(tipologias, "id", "nome"));
        setTemasOptions(convertListSelect(temas, "id", "nome"));
        setModalidadesOptions(convertListSelect(modalidades, "id", "nome"));
      } catch (error) {
        showError("Nao foi possivel carregar os filtros do relatorio.", error);
      } finally {
        setLoadingOptions(false);
      }
    };

    void loadOptions();
  }, [ensureCidades, ensureEstados, showError]);

  useEffect(() => {
    if (!possuiAmbitoEstadual) {
      setLotacoesSelecionadas([]);
    }
    if (!possuiAmbitoMunicipal) {
      setOrgaosSelecionados([]);
    }
  }, [possuiAmbitoEstadual, possuiAmbitoMunicipal]);

  useEffect(() => {
    if (periodError) {
      setAcoesOptions([]);
      setTurmasOptions([]);
      setAcoesSelecionadas([]);
      setTurmasSelecionadas([]);
      return;
    }

    const loadAcoes = async () => {
      try {
        setLoadingAcoes(true);
        const response = await findAcoesRelatorioGeral(dataInicial, dataFinal);
        setAcoesOptions((response.acoes || []).map((item) => ({ id: item.id, nome: item.nome })));
        setAcoesSelecionadas([]);
        setTurmasSelecionadas([]);
        setTurmasOptions([]);
      } catch (error) {
        setAcoesOptions([]);
        setTurmasOptions([]);
        setAcoesSelecionadas([]);
        setTurmasSelecionadas([]);
        showError("Nao foi possivel carregar as acoes do relatorio.", error);
      } finally {
        setLoadingAcoes(false);
      }
    };

    void loadAcoes();
  }, [dataFinal, dataInicial, periodError, showError]);

  useEffect(() => {
    if (periodError || selectedAcaoIds.length === 0) {
      setTurmasOptions([]);
      setTurmasSelecionadas([]);
      return;
    }

    const loadTurmas = async () => {
      try {
        setLoadingTurmas(true);
        const response = await findTurmasRelatorioGeral(selectedAcaoIds, dataInicial, dataFinal);
        const turmas = response.turmas || [];
        setTurmasOptions(turmas.map((item) => ({ id: item.id, nome: item.nome })));
        setTurmasSelecionadas((current) =>
          current.filter((id) => turmas.some((turma) => turma.id === Number(id))),
        );
      } catch (error) {
        setTurmasOptions([]);
        setTurmasSelecionadas([]);
        showError("Nao foi possivel carregar as turmas do relatorio.", error);
      } finally {
        setLoadingTurmas(false);
      }
    };

    void loadTurmas();
  }, [dataFinal, dataInicial, periodError, selectedAcaoIds, showError]);

  const buildParams = (): RelatorioTotalParticipacoesParams | null => {
    if (periodError) {
      return null;
    }

    return {
      data_inicio: dataInicial,
      data_fim: dataFinal,
      acoes_ids: selectedAcaoIds.length > 0 ? selectedAcaoIds : undefined,
      turmas_ids: selectedTurmaIds.length > 0 ? selectedTurmaIds : undefined,
      todos_municipios_estado: todosMunicipiosEstado || undefined,
      municipios_ids: !todosMunicipiosEstado && selectedMunicipioIds.length > 0 ? selectedMunicipioIds : undefined,
      ambitos_ids: selectedAmbitosIds.length > 0 ? selectedAmbitosIds : undefined,
      lotacoes_ids: possuiAmbitoEstadual && selectedLotacoesIds.length > 0 ? selectedLotacoesIds : undefined,
      orgaos_ids: possuiAmbitoMunicipal && selectedOrgaosIds.length > 0 ? selectedOrgaosIds : undefined,
      areas_atuacao_ids: selectedAreasIds.length > 0 ? selectedAreasIds : undefined,
      funcoes_ids: selectedFuncoesIds.length > 0 ? selectedFuncoesIds : undefined,
      vinculos_ids: selectedVinculosIds.length > 0 ? selectedVinculosIds : undefined,
      tipologias_ids: selectedTipologiasIds.length > 0 ? selectedTipologiasIds : undefined,
      temas_ids: selectedTemasIds.length > 0 ? selectedTemasIds : undefined,
      modalidades_ids: selectedModalidadesIds.length > 0 ? selectedModalidadesIds : undefined,
      idades: selectedIdades.length > 0 ? selectedIdades : undefined,
      cores_racas: selectedCoresRacas.length > 0 ? selectedCoresRacas : undefined,
      possui_deficiencia:
        possuiDeficiencia === "Sim" ? true : possuiDeficiencia === "Não" ? false : undefined,
    };
  };

  useEffect(() => {
    if (!hasRequiredFiltersForCount) {
      setEstimatedCount(null);
      return;
    }

    const params = buildParams();
    if (!params) {
      setEstimatedCount(null);
      return;
    }

    const loadCount = async () => {
      try {
        setLoadingCount(true);
        const response = await findRelatorioTotalQualificadosCount(params);
        setEstimatedCount(response.count ?? 0);
      } catch (error) {
        setEstimatedCount(null);
        showError("Nao foi possivel calcular o resultado estimado do relatorio.", error);
      } finally {
        setLoadingCount(false);
      }
    };

    void loadCount();
  }, [
    dataInicial,
    dataFinal,
    selectedAcaoIds,
    selectedTurmaIds,
    todosMunicipiosEstado,
    selectedMunicipioIds,
    selectedAmbitosIds,
    possuiAmbitoEstadual,
    possuiAmbitoMunicipal,
    selectedLotacoesIds,
    selectedOrgaosIds,
    selectedAreasIds,
    selectedFuncoesIds,
    selectedVinculosIds,
    selectedTipologiasIds,
    selectedTemasIds,
    selectedModalidadesIds,
    selectedIdades,
    selectedCoresRacas,
    possuiDeficiencia,
    hasRequiredFiltersForCount,
    showError,
  ]);

  const handleClear = () => {
    setDataInicial("");
    setDataFinal("");
    setAcoesSelecionadas([]);
    setTurmasSelecionadas([]);
    setTodosMunicipiosEstado(false);
    setMunicipiosSelecionados([]);
    setAmbitosSelecionados([]);
    setLotacoesSelecionadas([]);
    setOrgaosSelecionados([]);
    setAreasSelecionadas([]);
    setFuncoesSelecionadas([]);
    setVinculosSelecionados([]);
    setTipologiasSelecionadas([]);
    setTemasSelecionados([]);
    setModalidadesSelecionadas([]);
    setIdadesSelecionadas([]);
    setCoresRacasSelecionadas([]);
    setPossuiDeficiencia("");
    setEstimatedCount(null);
  };

  const handleGenerateReport = async () => {
    if (periodError) {
      showError(periodError);
      return;
    }

    const params = buildParams();
    if (!params) {
      showError("Informe a data inicial e a data final para gerar o relatorio.");
      return;
    }

    try {
      setGeneratingReport(true);
      const response = await findRelatorioTotalQualificados(params);
      const workbook = buildWorkbook((response.data || []) as QualificadosMunicipioItem[]);
      await Promise.all(workbook.worksheets.map((worksheet) => addGovernmentReportLogo(workbook, worksheet)));
      const excelBuffer = await workbook.xlsx.writeBuffer();

      saveAs(
        new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        "relatorio_total_qualificados.xlsx",
      );

      showSuccess("Relatorio gerado com sucesso.");
    } catch (error) {
      showError("Nao foi possivel gerar o relatorio.", error);
    } finally {
      setGeneratingReport(false);
    }
  };

  return (
    <>
      <div className={styles.reportFilterBody}>
        <div className={styles.reportHeaderRow}>
          <div className={styles.reportSelectedWrap}>
            <span className={styles.reportSelectedLabel}>Relatório selecionado:</span>
            <span className={styles.reportSelectedChip}>{selectedReportLabel}</span>
          </div>

          <div className={styles.exportFormatCard}>
            <div className={styles.exportFormatLabel}>Formato de exportação</div>
            <div className={styles.exportFormatValue}>
              <TableChartOutlinedIcon className={styles.exportFormatIcon} />
              XLSX
            </div>
          </div>
        </div>

        <div className={styles.reportDivider}></div>

        <div className={styles.filterSection}>
          <div className={styles.filterBlockTitle}>
            Período <span className={styles.optionalText}>(obrigatório)</span>
          </div>
          <div className={styles.filterGridTwo}>
            <div>
              <label className={stylesInput.titleInputSoft}>Data inicial</label>
              <TextField
                type="date"
                fullWidth
                size="small"
                variant="standard"
                value={dataInicial}
                error={Boolean(periodValidationError)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDataInicial(event.target.value)}
                InputProps={{ className: stylesInput.inputSoft, disableUnderline: true }}
              />
            </div>
            <div>
              <label className={stylesInput.titleInputSoft}>Data final</label>
              <TextField
                type="date"
                fullWidth
                size="small"
                variant="standard"
                value={dataFinal}
                error={Boolean(periodValidationError)}
                helperText={periodValidationError || undefined}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDataFinal(event.target.value)}
                InputProps={{ className: stylesInput.inputSoft, disableUnderline: true }}
              />
            </div>
          </div>
        </div>

        <div className={styles.filterSection}>
          <FormControlLabel
            control={
              <Checkbox
                checked={todosMunicipiosEstado}
                onChange={(event) => {
                  setTodosMunicipiosEstado(event.target.checked);
                  if (event.target.checked) {
                    setMunicipiosSelecionados([]);
                  }
                }}
              />
            }
            label="Todos municipios do estado"
          />
        </div>

        <div className={styles.filterSection}>
          <div className={styles.filterBlockTitle}>
            Municípios <span className={styles.optionalText}>(obrigatório)</span>
          </div>
          <MultiSelectCheck
            label=""
            placeholder="Selecione um ou mais municípios"
            fullWidth
            value={municipiosSelecionados}
            onChange={setMunicipiosSelecionados}
            options={municipiosOptions}
            disabled={todosMunicipiosEstado || loadingOptions}
            selectedDisplayMode="count"
            paperClassname={stylesInput.paper}
            popperClassname={stylesInput.popper}
            titleInputClassname={styles.reportHiddenLabel}
            inputSelectClassname={stylesInput.inputSoft}
            labelSelectClassname={stylesInput.labelSelect}
          />
        </div>

        <div className={styles.filterSection}>
          <div className={styles.filterBlockTitle}>
            Âmbitos <span className={styles.optionalText}>(obrigatório)</span>
          </div>
          <div className={styles.reportCheckboxGroup}>
            {AMBITO_OPTIONS.map((item) => (
              <FormControlLabel
                key={item.id}
                control={
                  <Checkbox
                    checked={ambitosSelecionados.includes(item.id)}
                    onChange={(event) => {
                      setAmbitosSelecionados((current) =>
                        event.target.checked
                          ? [...current, item.id]
                          : current.filter((value) => Number(value) !== item.id),
                      );
                    }}
                  />
                }
                label={item.nome}
              />
            ))}
          </div>
        </div>

        <div className={styles.filterGridTwo}>
          {possuiAmbitoEstadual && (
            <div className={styles.filterSection}>
              <div className={styles.filterBlockTitle}>
                Lotações <span className={styles.optionalText}>(opcional)</span>
              </div>
              <MultiSelectCheck
                label=""
                placeholder="Selecione uma ou mais lotações"
                fullWidth
                value={lotacoesSelecionadas}
                onChange={setLotacoesSelecionadas}
                options={lotacoesOptions}
                disabled={loadingOptions}
                selectedDisplayMode="count"
                paperClassname={stylesInput.paper}
                popperClassname={stylesInput.popper}
                titleInputClassname={styles.reportHiddenLabel}
                inputSelectClassname={stylesInput.inputSoft}
                labelSelectClassname={stylesInput.labelSelect}
              />
            </div>
          )}

          {possuiAmbitoMunicipal && (
            <div className={styles.filterSection}>
              <div className={styles.filterBlockTitle}>
                Órgãos de atuação <span className={styles.optionalText}>(opcional)</span>
              </div>
              <MultiSelectCheck
                label=""
                placeholder="Selecione um ou mais órgãos"
                fullWidth
                value={orgaosSelecionados}
                onChange={setOrgaosSelecionados}
                options={orgaosOptions}
                disabled={loadingOptions}
                selectedDisplayMode="count"
                paperClassname={stylesInput.paper}
                popperClassname={stylesInput.popper}
                titleInputClassname={styles.reportHiddenLabel}
                inputSelectClassname={stylesInput.inputSoft}
                labelSelectClassname={stylesInput.labelSelect}
              />
            </div>
          )}
        </div>

          <div className={styles.filterSection}>
            <div className={styles.filterBlockTitle}>
              Selecione a(s) ação(ões) <span className={styles.optionalText}>(opcional)</span>
            </div>
            <MultiSelectCheck
              label=""
              placeholder={
                periodError ? "Preencha primeiro a data inicial e a data final" : "Selecione uma ou mais ações"
              }
              fullWidth
              value={acoesSelecionadas}
              onChange={setAcoesSelecionadas}
              options={acoesOptions}
              disabled={Boolean(periodError) || loadingAcoes}
              selectedDisplayMode="count"
              paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper}
              titleInputClassname={styles.reportHiddenLabel}
              inputSelectClassname={stylesInput.inputSoft}
              labelSelectClassname={stylesInput.labelSelect}
            />
          </div>

          <div className={styles.filterSection}>
            <div className={styles.filterBlockTitle}>
              Selecione a(s) turma(s) <span className={styles.optionalText}>(opcional)</span>
            </div>
            <MultiSelectCheck
              label=""
              placeholder={
                selectedAcaoIds.length > 0
                  ? "Selecione uma ou mais turmas"
                  : "Selecione primeiro uma ou mais ações"
              }
              fullWidth
              value={turmasSelecionadas}
              onChange={setTurmasSelecionadas}
              options={turmasOptions}
              disabled={selectedAcaoIds.length === 0 || loadingTurmas}
              selectedDisplayMode="count"
              paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper}
              titleInputClassname={styles.reportHiddenLabel}
              inputSelectClassname={stylesInput.inputSoft}
              labelSelectClassname={stylesInput.labelSelect}
            />
          </div>

        <div className={styles.filterGridTwo}>
          <div className={styles.filterSection}>
            <div className={styles.filterBlockTitle}>Áreas de atuação</div>
            <MultiSelectCheck
              label=""
              placeholder="Selecione uma ou mais áreas"
              fullWidth
              value={areasSelecionadas}
              onChange={setAreasSelecionadas}
              options={areasOptions}
              disabled={loadingOptions}
              selectedDisplayMode="count"
              paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper}
              titleInputClassname={styles.reportHiddenLabel}
              inputSelectClassname={stylesInput.inputSoft}
              labelSelectClassname={stylesInput.labelSelect}
            />
          </div>
          <div className={styles.filterSection}>
            <div className={styles.filterBlockTitle}>Funções desempenhadas</div>
            <MultiSelectCheck
              label=""
              placeholder="Selecione uma ou mais funções"
              fullWidth
              value={funcoesSelecionadas}
              onChange={setFuncoesSelecionadas}
              options={funcoesOptions}
              disabled={loadingOptions}
              selectedDisplayMode="count"
              paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper}
              titleInputClassname={styles.reportHiddenLabel}
              inputSelectClassname={stylesInput.inputSoft}
              labelSelectClassname={stylesInput.labelSelect}
            />
          </div>
        </div>

        <div className={styles.filterGridTwo}>
          <div className={styles.filterSection}>
            <div className={styles.filterBlockTitle}>Vínculos empregatícios</div>
            <MultiSelectCheck
              label=""
              placeholder="Selecione um ou mais vínculos"
              fullWidth
              value={vinculosSelecionados}
              onChange={setVinculosSelecionados}
              options={vinculosOptions}
              disabled={loadingOptions}
              selectedDisplayMode="count"
              paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper}
              titleInputClassname={styles.reportHiddenLabel}
              inputSelectClassname={stylesInput.inputSoft}
              labelSelectClassname={stylesInput.labelSelect}
            />
          </div>
          <div className={styles.filterSection}>
            <div className={styles.filterBlockTitle}>Modalidades</div>
            <MultiSelectCheck
              label=""
              placeholder="Selecione uma ou mais modalidades"
              fullWidth
              value={modalidadesSelecionadas}
              onChange={setModalidadesSelecionadas}
              options={modalidadesOptions}
              disabled={loadingOptions}
              selectedDisplayMode="count"
              paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper}
              titleInputClassname={styles.reportHiddenLabel}
              inputSelectClassname={stylesInput.inputSoft}
              labelSelectClassname={stylesInput.labelSelect}
            />
          </div>
        </div>

        <div className={styles.filterSection}>
          <div className={styles.filterBlockTitle}>Tipologias</div>
          <MultiSelectCheck
            label=""
            placeholder="Selecione uma ou mais tipologias"
            fullWidth
            value={tipologiasSelecionadas}
            onChange={setTipologiasSelecionadas}
            options={tipologiasOptions}
            disabled={loadingOptions}
            selectedDisplayMode="count"
            paperClassname={stylesInput.paper}
            popperClassname={stylesInput.popper}
            titleInputClassname={styles.reportHiddenLabel}
            inputSelectClassname={stylesInput.inputSoft}
            labelSelectClassname={stylesInput.labelSelect}
          />
        </div>
        <div className={styles.filterSection}>
          <div className={styles.filterBlockTitle}>Temas</div>
          <MultiSelectCheck
            label=""
            placeholder="Selecione um ou mais temas"
            fullWidth
            value={temasSelecionados}
            onChange={setTemasSelecionados}
            options={temasOptions}
            disabled={loadingOptions}
            selectedDisplayMode="count"
            paperClassname={stylesInput.paper}
            popperClassname={stylesInput.popper}
            titleInputClassname={styles.reportHiddenLabel}
            inputSelectClassname={stylesInput.inputSoft}
            labelSelectClassname={stylesInput.labelSelect}
          />
        </div>

        <div className={styles.filterGridTwo}>
          <div className={styles.filterSection}>
            <div className={styles.filterBlockTitle}>Idades</div>
            <MultiSelectCheck
              label=""
              placeholder="Selecione uma ou mais idades"
              fullWidth
              value={idadesSelecionadas}
              onChange={setIdadesSelecionadas}
              options={AGE_OPTIONS}
              disabled={loadingOptions}
              selectedDisplayMode="count"
              paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper}
              titleInputClassname={styles.reportHiddenLabel}
              inputSelectClassname={stylesInput.inputSoft}
              labelSelectClassname={stylesInput.labelSelect}
            />
          </div>
          <div className={styles.filterSection}>
            <div className={styles.filterBlockTitle}>Cor/Raça</div>
            <MultiSelectCheck
              label=""
              placeholder="Selecione uma ou mais opções"
              fullWidth
              value={coresRacasSelecionadas}
              onChange={setCoresRacasSelecionadas}
              options={COR_RACA_OPTIONS}
              disabled={loadingOptions}
              selectedDisplayMode="count"
              paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper}
              titleInputClassname={styles.reportHiddenLabel}
              inputSelectClassname={stylesInput.inputSoft}
              labelSelectClassname={stylesInput.labelSelect}
            />
          </div>
          <div className={styles.filterSection}>
            <div className={styles.filterBlockTitle}>Pessoas com deficiência</div>
            <SelectField
              label=""
              placeholder="Selecione"
              value={possuiDeficiencia}
              onChange={setPossuiDeficiencia}
              options={DEFICIENCIA_OPTIONS}
              disableTyping
              inputClassName={stylesInput.inputSoft}
              containerClassName={styles.reportSelectContainer}
            />
          </div>
        </div>

        <div className={styles.reportInfoBanner}>
          <InfoOutlinedIcon className={styles.reportInfoBannerIcon} />
          <span>
            A exportação agrupa por município e mescla as colunas de totais, exibindo uma linha por ação participante.
          </span>
        </div>

        <div className={styles.selectionSummaryCard}>
          <div className={styles.selectionSummaryTitle}>Resumo da seleção</div>
          <div className={styles.selectionSummaryGrid}>
            <div className={styles.selectionSummaryItem}>
              <CalendarMonthOutlinedIcon className={styles.selectionSummaryIcon} />
              <div>
                <div className={styles.selectionSummaryLabel}>Período</div>
                <div className={styles.selectionSummaryValue}>
                  {formatDate(dataInicial)} a {formatDate(dataFinal)}
                </div>
              </div>
            </div>
            <div className={styles.selectionSummaryItem}>
              <PlaceOutlinedIcon className={styles.selectionSummaryIcon} />
              <div>
                <div className={styles.selectionSummaryLabel}>Municípios</div>
                <div className={styles.selectionSummaryValue}>
                  {todosMunicipiosEstado ? "Todos do estado" : `${selectedMunicipioIds.length} selecionado(s)`}
                </div>
              </div>
            </div>
            <div className={styles.selectionSummaryItem}>
              <Diversity3OutlinedIcon className={styles.selectionSummaryIcon} />
              <div>
                <div className={styles.selectionSummaryLabel}>Âmbitos</div>
                <div className={styles.selectionSummaryValue}>{selectedAmbitosIds.length} selecionado(s)</div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.reportActionsRow}>
          <Button
            variant="outlined"
            className={stylesButton.buttonSoftMuted}
            startIcon={<DeleteOutlineOutlinedIcon />}
            onClick={handleClear}
          >
            Limpar filtros
          </Button>

          <div className={styles.reportActionsRight}>
            <div className={styles.estimatedResultCard}>
              <div className={styles.estimatedResultLabel}>Resultado estimado</div>
              <div className={styles.estimatedResultValue}>
                {loadingCount ? <CircularProgress size={18} /> : estimatedCount ?? "-"}
              </div>
              <div className={styles.estimatedResultMeta}>registros</div>
            </div>

            <div className={styles.generateButtonGroup}>
              <Button
                variant="contained"
                className={styles.generateButton}
                startIcon={<DownloadOutlinedIcon />}
                onClick={handleGenerateReport}
                disabled={generatingReport || loadingOptions}
              >
                Gerar relatório
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Backdrop
        open={generatingReport}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 100,
          flexDirection: "column",
          gap: 2,
          backgroundColor: "rgba(15, 23, 42, 0.45)",
        }}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ fontWeight: 600 }}>Gerando relatório...</Typography>
      </Backdrop>
    </>
  );
};

export default RelatorioTotalQualificados;
